import { useEffect, useReducer } from "react";
import "../lightcast/Lightcast.scss";
import translate from "@/i18n/translate";
import { sendRequest } from "@/common/http";
import { RegionalContext, County } from "../lightcast/Interface";
import ApiError from "../common/elements/ApiError";
export interface InstitutionRegionContextProps {
  regionalContext: RegionalContext | null;
  onComponentLoad: (component: string) => void;
}

interface InstitutionRegionContextAction {
  type: string;
  data: County[];
}
interface InstitutionRegionContextState {
  regionalPopulation: County[];
  apiError: boolean;
}
const initialState = {
  regionalPopulation: [],
  apiError: false
};
const defaultInstitutionRegionContextAction = {
  type: "",
  data: []
};

interface BodyProps {
  regionalContext: RegionalContext | null;
  regionalPopulation: County[];
}

const reducer = (
  state: InstitutionRegionContextState,
  action: InstitutionRegionContextAction
) => {
  switch (action.type) {
    case "setPopulationByCounty":
      return { ...state, regionalPopulation: action.data };
    case "setPopulationByCountryError":
      return {
        ...state,
        apiError: true
      };
    default:
      return state;
  }
};
export default function InstitutionRegionContext({
  regionalContext,
  onComponentLoad
}: InstitutionRegionContextProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const applyResult = (result: County[]) => {
      dispatch({
        ...defaultInstitutionRegionContextAction,
        type: "setPopulationByCounty",
        data: result
      });
      onComponentLoad("institutionRegionContext");
    };

    const onError = () => {
      dispatch({
        ...defaultInstitutionRegionContextAction,
        type: "setPopulationByCountryError"
      });

      onComponentLoad("institutionRegionContext");
    };

    sendRequest(
      { uri: "/lightcast/population_by_county", camelize: true },
      applyResult,
      onError
    );
  }, []);

  return (
    <div className="grid-container wm-grid-col-12 wm-grid-col-lg-12 wm-grid-col-md-10 wm-grid-col-sm-8">
      <div className="">
        <div className="header">
          <div className="info">
            <h3 className="title instituition-context-title">
              {translate("app.institutionRegionContext.indusRegionContext")}
            </h3>
            <div className="description instituition-context-desc">
              {translate("app.institutionRegionContext.reviewLastFive")}
            </div>
          </div>
        </div>
        {!state.apiError ? (
          <div className="body wm-grid">
            <Body
              regionalContext={regionalContext}
              regionalPopulation={state.regionalPopulation}
            />
          </div>
        ) : (
          <ApiError />
        )}
      </div>
    </div>
  );
}

const Body = (props: BodyProps) => {
  return (
    <div className="institution-context-widget-container">
      <div className="institution-detail">
        {translate("app.lightcast.institutionDetails")}
      </div>
      <div className="body context-widget-labor-force">
        <div className="instituion-context-ipeds -detail">
          <div className="-detail-value">
            <div className="context-count">{props.regionalContext?.zip}</div>
            <div className="context-label">
              {translate("app.institutionRegionContext.institutionIPEDS")}
            </div>
          </div>
        </div>
        <div className="instituion-context-detail -detail">
          <div className="-detail-value">
            <div className="context-count">
              {props.regionalContext?.radius} mi
            </div>
            <div className="context-label">
              {translate("app.institutionRegionContext.radiusZipCode")}
            </div>
          </div>
        </div>
        <div className="instituion-context-detail -detail context-time-frame">
          <div className="-detail-value">
            <div className="context-count">
              {props.regionalContext?.timeFrame}
            </div>
            <div className="context-label">
              {translate("app.institutionRegionContext.fiveYear")}
            </div>
          </div>
        </div>
        <div className="instituion-context-counties">
          <div className="-detail-value">
            <div className="context-count">
              {props.regionalContext?.noOfCounties}
            </div>
            <div className="context-label">
              {translate("app.institutionRegionContext.countiesIncluded")}
            </div>
          </div>
        </div>
      </div>
      <div className="county-container -context">
        <div className="-header county-header">
          {translate("app.institutionRegionContext.regionalPopulation")}
        </div>
        <table className="data-table -shadow">
          <thead className="dataheader">
            <tr className="dataheaderrow">
              <th className="dataheadercell" data-th="County">
                {translate("app.institutionRegionContext.county")}
              </th>
              <th className="dataheadercell" data-th="Population">
                {translate("app.institutionRegionContext.population")}
              </th>
              <th className="dataheadercell" data-th="Percentage">
                {translate("app.institutionRegionContext.percentage")}
              </th>
            </tr>
          </thead>
          <tbody className="databody">
            {props.regionalPopulation &&
              props.regionalPopulation.map((rp: County) => {
                return (
                  <tr className="datarow" key={rp?.name}>
                    <td className="datacell">{rp?.name}</td>
                    <td className="datacell">{rp?.population}</td>
                    <td className="datacell">{rp?.percentage}%</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
